import { Button } from "@cloudscape-design/components";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { isSome } from "../../../../utils/sugarUtils";
import { ServicesListItem } from "./ServicesListItem";
import { servicesList } from "./menuConfig";

interface MenuDrawerProps {
  visible: boolean;
  favoriteServices: Object;
  handleFavorites: (path: string, label: string) => void;
  onClose: () => void;
}

type MobilePane = "categories" | "items";

export const MenuDrawer = forwardRef<HTMLDivElement, MenuDrawerProps>(
  ({ visible, favoriteServices, handleFavorites, onClose }, ref) => {
    const [selectedGroup, setSelectedGroup] = useState<string>("favorites");
    const [mobilePane, setMobilePane] = useState<MobilePane>("categories");

    useEffect(() => {
      if (!visible) {
        setMobilePane("categories");
        return;
      }
      const mq = window.matchMedia("(max-width: 767px)");
      if (!mq.matches) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }, [visible]);

    const getContent = (services: (typeof servicesList)[number]) => {
      const isFavoriteSelected = selectedGroup === "favorites";
      const label = isFavoriteSelected ? "Favorites" : services.label;
      const items = isFavoriteSelected
        ? Object.keys(favoriteServices).map((key) => ({
            path: key,
            label: favoriteServices[key] as string,
            description: "",
          }))
        : services.items ?? [];

      return { label, items, path: services.path };
    };

    const selectedServices = useMemo(
      () => servicesList.find((item) => item.key === selectedGroup),
      [selectedGroup],
    );
    const { label: activeSectionLabel, items: activeItems } = selectedServices
      ? getContent(selectedServices)
      : { label: "", items: [] as { path: string; label: string; description: string }[] };

    if (!visible) return null;

    const handleSelectGroup = (key: string) => {
      const service = servicesList.find((item) => item.key === key);
      if (service == null) return;
      if ((service.items == null || service.items.length == 0) && service.path != null) {
        window.location.href = service.path;
        return;
      } else {
        setSelectedGroup(key);
      }
    };

    const categoryList = (
      <ul className="overflow-y-auto h-full px-4 pb-6 pt-2 md:px-0 md:pt-0 md:pb-0">
        {servicesList.map(({ label, key, isNew=false }) => (
          <li
            key={key}
            className="text-lg first:mb-3 first:pb-3 mb-3 first:border-b first:border-border text-white hover:text-link"
          >
            <button
              type="button"
              onClick={() => {
                handleSelectGroup(key);
                if (window.matchMedia("(max-width: 767px)").matches) {
                  setMobilePane("items");
                }
              }}
              className="w-full text-left"
            >
              {label}
              {isNew && (
                <span className="text-xs text-gray-500">New</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    );

    const itemsPanel = selectedServices && (
      <div className="text-textDefaultColor h-full flex flex-col min-h-0">
        <div className="overflow-y-auto pr-4 h-full pb-6 pl-4 pt-2">
          <ul>
            {activeItems.map(({ label, path, description }) => (
              <ServicesListItem
                key={path}
                path={path}
                isFavorite={isSome((favoriteServices as Record<string, string>)[path])}
                handleFavorites={() => handleFavorites(path, label)}
              >
                <h4 className="text-lg text-heading group-hover:text-link font-bold">{label}</h4>
                <p className="text-xs text-subtitle">{description}</p>
              </ServicesListItem>
            ))}
          </ul>
        </div>
      </div>
    );

    return (
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <style>
          {`
          section[class^='awsui_mobile-toolbar_'] {
            display: none !important;
          }
          `}
        </style>

        {/* Mobile: fullscreen two-step menu */}
        <div className="md:hidden fixed inset-0 z-[10000] flex flex-col bg-background border-t border-border shadow-[0px_10px_20px_0px_rgba(0,0,0,0.5)] animate-popoverOpen">
          <header className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center gap-2 px-3 py-3 border-b border-border shrink-0">
            {mobilePane === "items" ? (
              <Button
                iconName="angle-left"
                variant="icon"
                onClick={() => setMobilePane("categories")}
                ariaLabel="Back to service categories"
              />
            ) : (
              <span className="w-10" aria-hidden />
            )}
            <h1 className="text-center text-lg font-bold text-white truncate min-w-0">
              {mobilePane === "categories" ? "Services" : activeSectionLabel}
            </h1>
            <Button iconName="close" variant="icon" onClick={onClose} ariaLabel="Close menu" />
          </header>
          <div className="flex-1 min-h-0 overflow-hidden">
            <div
              className="flex h-full w-[200%] transition-transform duration-300 ease-out motion-reduce:transition-none"
              style={{
                transform: mobilePane === "items" ? "translateX(-50%)" : "translateX(0)",
              }}
            >
              <div className="w-1/2 h-full min-h-0 flex flex-col">{categoryList}</div>
              <div className="w-1/2 h-full min-h-0 flex flex-col border-l border-border">
                {itemsPanel}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: anchored two-column panel */}
        <div
          className="hidden md:block z-[10000] animate-popoverOpen absolute top-20 md:top-11 left-4 md:left-24 w-[45rem] mt-2.5 rounded-lg bg-background border-borderControl border-2 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.5)] pt-6 pl-6"
          style={{ height: "80vh", overflow: "hidden" }}
        >
          <div className="flex space-x-4 h-full" style={{ overflow: "hidden" }}>
            <ul className="basis-2/5 overflow-y-auto h-full">
              {servicesList.map(({ label, key, isNew=false, isUpdated=false }) => (
                <li
                  key={key}
                  className={`text-lg first:mb-3 first:pb-3 mb-3 first:border-b first:border-border hover:text-link ${selectedGroup === key ? "text-link font-bold" : "text-white"}`}
                >
                  <button type="button" onClick={() => handleSelectGroup(key)} className="w-full text-left">
                    {label}
                    {isNew && (
                      <span className="text-xs bg-blue-600 text-white font-bold px-2 py-1 rounded-md ml-2">New</span>
                    )}
                    {isUpdated && (
                      <span className="text-xs bg-green-600 text-white font-bold px-2 py-1 rounded-md ml-2">Updated</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex space-x-4 basis-3/5 h-full">
              <div className="border-l border-border mb-6" />
              <div className="w-full h-full">
                {servicesList
                  .filter((item) => item.key === selectedGroup)
                  .map((services) => {
                    const { label, items } = getContent(services);
                    return (
                      <div key={label} className="text-textDefaultColor h-full flex flex-col space-y-4">
                        <div className="text-2xl font-bold text-subtitle ml-10">{label}</div>
                        <div className="overflow-y-auto pr-5 h-full pb-5">
                          <ul>
                            {(items as any).map(({ label, path, description, isNew=false, isUpdated=false }) => (
                              <ServicesListItem
                                key={path}
                                path={path}
                                isFavorite={isSome((favoriteServices as Record<string, string>)[path])}
                                handleFavorites={() => handleFavorites(path, label)}
                              >
                                <h4 className="text-lg text-heading group-hover:text-link font-bold">
                                  {label}
                                  {isNew && (
                                    <span className="text-xs bg-blue-600 text-white font-bold px-2 py-1 rounded-md ml-2">New</span>
                                  )}
                                  {isUpdated && (
                                    <span className="text-xs bg-green-600 text-white font-bold px-2 py-1 rounded-md ml-2">Updated</span>
                                  )}
                                </h4>
                                <p className="text-xs text-subtitle">{description}</p>
                              </ServicesListItem>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Button iconName="close" variant="icon" onClick={onClose} />
          </div>
        </div>
      </div>
    );
  },
);
